/**
 * Vehicle layer using MapLibre symbol layer with GeoJSON source.
 *
 * Renders vehicles as colored circle badges with transport mode icons.
 *
 * Features:
 * - Smooth animation via requestAnimationFrame loop
 * - Selection dimming: non-selected vehicles dim to 0.4 opacity
 * - Admin-only freshness dots (V4 left, V1-DA right) beneath markers
 * - Respects prefers-reduced-motion system setting
 *
 * PERFORMANCE: Direct source.setData() calls for efficient 60fps updates
 * without triggering React re-renders.
 */

import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useMap, Source, Layer } from 'react-map-gl/maplibre';
import type { GeoJSONSource } from 'maplibre-gl';
import type { CircleLayerSpecification, SymbolLayerSpecification } from 'maplibre-gl';
import type { Feature, FeatureCollection, Point } from 'geojson';

import { useVehicleStore } from '../../stores/vehicleStore';
import { useFilterStore } from '../../stores/filterStore';
import { useSettingsStore, selectSmoothAnimationEnabled } from '../../stores/settingsStore';
import { useAuthStore } from '../../stores/authStore';
import { useAnimationLoop } from '../../hooks/useAnimationLoop';
import { interpolatePosition } from '../../utils/interpolation';
import { RENDER_CONFIG } from '../../config';
import { getFreshnessLevel } from '../../utils/freshness';
import type { FreshnessLevel } from '../../utils/freshness';
import type { VehicleState } from '../../stores/vehicleStore';
import { getIconForMode } from '../../utils/vehicleIcons';
import { getContrastingIconColor } from '../../utils/colors';

import { vehicleLayerMetrics } from './vehicleLayerMetrics';

const VEHICLE_SOURCE_ID = 'vehicles-source';
const VEHICLE_FRESHNESS_V4_LAYER_ID = 'vehicles-freshness-dot-v4';
const VEHICLE_FRESHNESS_V1DA_LAYER_ID = 'vehicles-freshness-dot-v1da';
const VEHICLE_AT_STOP_RING_LAYER_ID = 'vehicles-at-stop-ring';
const VEHICLE_BACKGROUND_LAYER_ID = 'vehicles-background';
const VEHICLE_ICON_LAYER_ID = 'vehicles-icons';
const VEHICLE_LABEL_LAYER_ID = 'vehicles-labels';

interface VehicleFeatureProperties {
  id: string;
  journey_gid: string;
  line_name: string;
  transport_mode: string;
  color_bg: string;
  color_fg: string;
  heading: number | null;
  speed_kmh: number | null;
  // Computed visual properties
  display_color: string;
  display_opacity: number;
  // Icon for transport mode
  icon_id: string;
  // V1 data availability and at-stop state
  has_v1_data: boolean;
  at_stop_v1: boolean;
  // Adaptive icon color (black/white based on line color luminance)
  icon_color: string;
  // Freshness dot colors (hex)
  v4_dot_color: string;
  v1da_dot_color: string;
  // Whether freshness dots should be shown
  show_freshness_dots: boolean;
}

/** Map FreshnessLevel to hex color for MapLibre circle layers */
function freshnessToHex(level: FreshnessLevel | null): string {
  const colors = RENDER_CONFIG.FRESHNESS_DOT.COLORS;
  switch (level) {
    case 'fresh': return colors.fresh;
    case 'warning': return colors.warning;
    case 'stale': return colors.stale;
    default: return colors.none;
  }
}

/**
 * Check if a line matches any of the active filters.
 */
function matchesLineFilter(lineName: string, lineFilters: string[]): boolean {
  if (lineFilters.length === 0) {
    return true;
  }
  const normalizedName = lineName.toUpperCase();
  return lineFilters.some((filter) => normalizedName === filter);
}

/**
 * Convert vehicles to GeoJSON feature collection for rendering.
 * Uses interpolated positions when smooth animation is enabled.
 */
function vehiclesToGeoJSON(
  vehicles: Map<string, VehicleState>,
  visibleModes: string[],
  lineFilters: string[],
  useInterpolation: boolean,
  selectedVehicleId: string | null,
  showFreshnessDots: boolean,
): FeatureCollection<Point, VehicleFeatureProperties> {
  const features: Feature<Point, VehicleFeatureProperties>[] = [];
  const now = Date.now();

  vehicles.forEach((vehicle) => {
    // Skip dead vehicles
    if (vehicle.visualState === 'dead') {
      return;
    }

    // Filter by transport mode
    const transportMode = vehicle.line?.transport_mode ?? 'bus';
    if (!visibleModes.includes(transportMode)) {
      return;
    }

    // Filter by line name
    const lineName = vehicle.line?.short_name ?? '';
    if (!matchesLineFilter(lineName, lineFilters)) {
      return;
    }

    // Calculate display properties
    const baseColor = vehicle.line?.color_bg || '#666666';

    // Selection dimming: selected = 1.0, others = MAP_DIM_ON_SELECT when something is selected
    let displayOpacity = 1.0;
    if (selectedVehicleId !== null && vehicle.service_journey_id !== selectedVehicleId) {
      displayOpacity = RENDER_CONFIG.MAP_DIM_ON_SELECT;
    }

    // Get icon for transport mode
    const iconId = getIconForMode(transportMode);

    // V1 data availability
    const hasV1Data = vehicle.v1LastUpdateTime !== null;

    // At stop with V1 GPS data
    const atStopV1 = vehicle.at_stop && vehicle.data_source === 'v1-da-gps';

    // Icon color adapts to line color for contrast
    const iconColor = getContrastingIconColor(baseColor);

    // Freshness dot colors
    const v4DotColor = freshnessToHex(getFreshnessLevel(vehicle.v4LastUpdateTime, now));
    const v1daDotColor = freshnessToHex(getFreshnessLevel(vehicle.v1LastUpdateTime, now));

    // Use interpolated position if enabled, otherwise use target directly.
    // Main marker always uses V1-DA GPS position (targetLatitude/targetLongitude).
    // V5 segment interpolation is only used in V5PositionLayer for admin debug visualization.
    let lat: number;
    let lon: number;

    if (useInterpolation) {
      const [interpolatedLat, interpolatedLon] = interpolatePosition(
        vehicle.latitude,
        vehicle.longitude,
        vehicle.targetLatitude,
        vehicle.targetLongitude,
        vehicle.lastUpdateTime,
        now,
        RENDER_CONFIG.ANIMATION_DURATION_MS
      );
      lat = interpolatedLat;
      lon = interpolatedLon;
    } else {
      lat = vehicle.targetLatitude;
      lon = vehicle.targetLongitude;
    }

    features.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [lon, lat],
      },
      properties: {
        id: vehicle.service_journey_id,
        journey_gid: vehicle.journey_gid,
        line_name: vehicle.line?.short_name ?? '',
        transport_mode: transportMode,
        color_bg: baseColor,
        color_fg: vehicle.line?.color_fg || '#ffffff',
        heading: vehicle.heading,
        speed_kmh: vehicle.speed_kmh,
        display_color: baseColor,
        display_opacity: displayOpacity,
        icon_id: iconId,
        has_v1_data: hasV1Data,
        at_stop_v1: atStopV1,
        icon_color: iconColor,
        v4_dot_color: v4DotColor,
        v1da_dot_color: v1daDotColor,
        show_freshness_dots: showFreshnessDots,
      },
    });
  });

  // Limit to max rendered vehicles
  return {
    type: 'FeatureCollection',
    features: features.slice(0, RENDER_CONFIG.MAX_RENDERED_VEHICLES),
  };
}

export interface VehicleLayerProps {
  /** Whether vehicle icons have been registered with the map */
  iconsReady?: boolean;
}

export const VehicleLayer = memo(function VehicleLayer({ iconsReady = false }: VehicleLayerProps) {
  const { current: map } = useMap();
  const vehicles = useVehicleStore((state) => state.vehicles);
  const selectedVehicleId = useVehicleStore((state) => state.selectedVehicleId);
  const selectVehicle = useVehicleStore((state) => state.selectVehicle);
  const enabledModes = useFilterStore((state) => state.enabledModes);
  const zoomLevel = useFilterStore((state) => state.zoomLevel);
  const lineFilters = useFilterStore((state) => state.lineFilters);
  const getVisibleModes = useFilterStore((state) => state.getVisibleModes);

  // Animation settings
  const smoothAnimationEnabled = useSettingsStore(selectSmoothAnimationEnabled);

  // Admin freshness dots
  const isAdmin = useAuthStore((state) => state.user)?.role === 'admin';
  const showFreshnessDots = useSettingsStore((state) => state.showFreshnessDots);
  const dotsEnabled = isAdmin && showFreshnessDots;

  // Check for prefers-reduced-motion
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  // Store refs for animation callback (avoid recreating callback on every render)
  const vehiclesRef = useRef(vehicles);
  const visibleModesRef = useRef<string[]>([]);
  const lineFiltersRef = useRef(lineFilters);
  const selectedVehicleIdRef = useRef(selectedVehicleId);
  const dotsEnabledRef = useRef(dotsEnabled);

  // Get visible transport modes
  const visibleModes = useMemo(() => getVisibleModes(), [enabledModes, zoomLevel, getVisibleModes]);

  // Keep refs updated
  useEffect(() => { vehiclesRef.current = vehicles; }, [vehicles]);
  useEffect(() => { visibleModesRef.current = visibleModes; }, [visibleModes]);
  useEffect(() => { lineFiltersRef.current = lineFilters; }, [lineFilters]);
  useEffect(() => { selectedVehicleIdRef.current = selectedVehicleId; }, [selectedVehicleId]);
  useEffect(() => { dotsEnabledRef.current = dotsEnabled; }, [dotsEnabled]);

  // Animation is active when: enabled in settings AND not prefers-reduced-motion AND map exists
  const animationActive = smoothAnimationEnabled && !prefersReducedMotion.current;

  // Initial GeoJSON for the Source component
  // NOTE: iconsReady is included to force re-render when icons become available
  const initialGeojson = useMemo(
    () => vehiclesToGeoJSON(vehicles, visibleModes, lineFilters, false, selectedVehicleId, dotsEnabled),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleModes, lineFilters, iconsReady]
  );

  // Animation frame callback - updates GeoJSON directly on the source
  const animationCallback = useCallback(() => {
    if (!map) return;

    const source = map.getSource(VEHICLE_SOURCE_ID) as GeoJSONSource | undefined;
    if (!source) return;

    const t0 = performance.now();
    const geojson = vehiclesToGeoJSON(
      vehiclesRef.current,
      visibleModesRef.current,
      lineFiltersRef.current,
      animationActive,
      selectedVehicleIdRef.current,
      dotsEnabledRef.current,
    );
    vehicleLayerMetrics.lastSerializationMs = performance.now() - t0;
    vehicleLayerMetrics.renderedFeatures = geojson.features.length;
    vehicleLayerMetrics.setDataCount++;

    source.setData(geojson);
  }, [map, animationActive]);

  // Run animation loop when active
  useAnimationLoop(animationCallback, animationActive && !!map);

  // When animation is disabled, update source on every vehicle/filter change.
  useEffect(() => {
    if (animationActive || !map) return;

    const source = map.getSource(VEHICLE_SOURCE_ID) as GeoJSONSource | undefined;
    if (!source) return;

    const geojson = vehiclesToGeoJSON(vehicles, visibleModes, lineFilters, false, selectedVehicleId, dotsEnabled);
    vehicleLayerMetrics.renderedFeatures = geojson.features.length;
    vehicleLayerMetrics.setDataCount++;
    source.setData(geojson);
  }, [vehicles, visibleModes, lineFilters, animationActive, map, iconsReady, selectedVehicleId, dotsEnabled]);

  // When animation IS active, push data on filter/selection/icon changes only.
  // Vehicle position updates are handled by the rAF loop (via animationCallback).
  // Background→foreground recovery is handled by useAnimationLoop's immediate
  // callback on visibility resume.
  useEffect(() => {
    if (!animationActive || !map) return;

    const source = map.getSource(VEHICLE_SOURCE_ID) as GeoJSONSource | undefined;
    if (!source) return;

    const geojson = vehiclesToGeoJSON(
      vehiclesRef.current, visibleModes, lineFilters, false, selectedVehicleId, dotsEnabled,
    );
    vehicleLayerMetrics.renderedFeatures = geojson.features.length;
    vehicleLayerMetrics.setDataCount++;
    source.setData(geojson);
  }, [visibleModes, lineFilters, animationActive, map, iconsReady, selectedVehicleId, dotsEnabled]);

  // Freshness dot V4 layer (left dot)
  const freshnessV4LayerStyle: CircleLayerSpecification = useMemo(
    () => ({
      id: VEHICLE_FRESHNESS_V4_LAYER_ID,
      type: 'circle',
      source: VEHICLE_SOURCE_ID,
      filter: ['==', ['get', 'show_freshness_dots'], true],
      paint: {
        'circle-radius': RENDER_CONFIG.FRESHNESS_DOT.RADIUS,
        'circle-color': ['get', 'v4_dot_color'],
        'circle-translate': RENDER_CONFIG.FRESHNESS_DOT.V4_TRANSLATE as [number, number],
        'circle-opacity': ['get', 'display_opacity'],
      },
    }),
    []
  );

  // Freshness dot V1-DA layer (right dot)
  const freshnessV1daLayerStyle: CircleLayerSpecification = useMemo(
    () => ({
      id: VEHICLE_FRESHNESS_V1DA_LAYER_ID,
      type: 'circle',
      source: VEHICLE_SOURCE_ID,
      filter: ['==', ['get', 'show_freshness_dots'], true],
      paint: {
        'circle-radius': RENDER_CONFIG.FRESHNESS_DOT.RADIUS,
        'circle-color': ['get', 'v1da_dot_color'],
        'circle-translate': RENDER_CONFIG.FRESHNESS_DOT.V1DA_TRANSLATE as [number, number],
        'circle-opacity': ['get', 'display_opacity'],
      },
    }),
    []
  );

  // At-stop ring layer (white ring behind markers for V1 at-stop vehicles)
  const atStopRingLayerStyle: CircleLayerSpecification = useMemo(
    () => ({
      id: VEHICLE_AT_STOP_RING_LAYER_ID,
      type: 'circle',
      source: VEHICLE_SOURCE_ID,
      filter: ['==', ['get', 'at_stop_v1'], true],
      paint: {
        'circle-radius': 15,
        'circle-color': 'transparent',
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 3,
        'circle-opacity': ['get', 'display_opacity'],
        'circle-stroke-opacity': ['get', 'display_opacity'],
      },
    }),
    []
  );

  // Background circle layer (colored badge, no stroke)
  const backgroundLayerStyle: CircleLayerSpecification = useMemo(
    () => ({
      id: VEHICLE_BACKGROUND_LAYER_ID,
      type: 'circle',
      source: VEHICLE_SOURCE_ID,
      paint: {
        'circle-radius': 12,
        'circle-color': ['get', 'display_color'],
        'circle-stroke-width': 0,
        'circle-opacity': ['get', 'display_opacity'],
      },
    }),
    []
  );

  // Icon layer (SDF icons with adaptive color based on line background)
  const iconLayerStyle: SymbolLayerSpecification = useMemo(
    () => ({
      id: VEHICLE_ICON_LAYER_ID,
      type: 'symbol',
      source: VEHICLE_SOURCE_ID,
      layout: {
        'icon-image': ['get', 'icon_id'],
        'icon-size': 0.55,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-anchor': 'center',
        'symbol-sort-key': ['get', 'line_name'],
      },
      paint: {
        'icon-opacity': ['get', 'display_opacity'],
        'icon-color': ['get', 'icon_color'],
      },
    }),
    []
  );

  // Label layer (line number below icon)
  const labelLayerStyle: SymbolLayerSpecification = useMemo(
    () => ({
      id: VEHICLE_LABEL_LAYER_ID,
      type: 'symbol',
      source: VEHICLE_SOURCE_ID,
      layout: {
        'text-field': ['get', 'line_name'],
        'text-size': 10,
        'text-offset': [0, 1.7],
        'text-anchor': 'top',
        'text-allow-overlap': false,
        'symbol-sort-key': ['get', 'line_name'],
      },
      paint: {
        'text-color': '#333333',
        'text-halo-color': '#ffffff',
        'text-halo-width': 1,
        'text-opacity': ['get', 'display_opacity'],
      },
    }),
    []
  );

  // Handle click on vehicle
  const handleClick = useCallback(
    (e: maplibregl.MapMouseEvent) => {
      if (!map) return;

      const features = map.queryRenderedFeatures(e.point, {
        layers: [VEHICLE_AT_STOP_RING_LAYER_ID, VEHICLE_BACKGROUND_LAYER_ID, VEHICLE_ICON_LAYER_ID],
      });

      if (features.length > 0) {
        const feature = features[0];
        const id = feature.properties?.id;
        if (id) {
          selectVehicle(id);
        }
      } else {
        selectVehicle(null);
      }
    },
    [map, selectVehicle]
  );

  // Handle hover
  const handleMouseEnter = useCallback(() => {
    if (map) {
      map.getCanvas().style.cursor = 'pointer';
    }
  }, [map]);

  const handleMouseLeave = useCallback(() => {
    if (map) {
      map.getCanvas().style.cursor = '';
    }
  }, [map]);

  // Set up event listeners on all vehicle layers
  useEffect(() => {
    if (!map) return;

    map.on('click', VEHICLE_AT_STOP_RING_LAYER_ID, handleClick);
    map.on('click', VEHICLE_BACKGROUND_LAYER_ID, handleClick);
    map.on('click', VEHICLE_ICON_LAYER_ID, handleClick);
    map.on('mouseenter', VEHICLE_AT_STOP_RING_LAYER_ID, handleMouseEnter);
    map.on('mouseenter', VEHICLE_BACKGROUND_LAYER_ID, handleMouseEnter);
    map.on('mouseenter', VEHICLE_ICON_LAYER_ID, handleMouseEnter);
    map.on('mouseleave', VEHICLE_AT_STOP_RING_LAYER_ID, handleMouseLeave);
    map.on('mouseleave', VEHICLE_BACKGROUND_LAYER_ID, handleMouseLeave);
    map.on('mouseleave', VEHICLE_ICON_LAYER_ID, handleMouseLeave);

    return () => {
      map.off('click', VEHICLE_AT_STOP_RING_LAYER_ID, handleClick);
      map.off('click', VEHICLE_BACKGROUND_LAYER_ID, handleClick);
      map.off('click', VEHICLE_ICON_LAYER_ID, handleClick);
      map.off('mouseenter', VEHICLE_AT_STOP_RING_LAYER_ID, handleMouseEnter);
      map.off('mouseenter', VEHICLE_BACKGROUND_LAYER_ID, handleMouseEnter);
      map.off('mouseenter', VEHICLE_ICON_LAYER_ID, handleMouseEnter);
      map.off('mouseleave', VEHICLE_AT_STOP_RING_LAYER_ID, handleMouseLeave);
      map.off('mouseleave', VEHICLE_BACKGROUND_LAYER_ID, handleMouseLeave);
      map.off('mouseleave', VEHICLE_ICON_LAYER_ID, handleMouseLeave);
    };
  }, [map, handleClick, handleMouseEnter, handleMouseLeave]);

  return (
    <Source id={VEHICLE_SOURCE_ID} type="geojson" data={initialGeojson}>
      {/* Layer order: freshness dots, at-stop ring, background circle, icon, label (bottom to top) */}
      <Layer {...freshnessV4LayerStyle} />
      <Layer {...freshnessV1daLayerStyle} />
      <Layer {...atStopRingLayerStyle} />
      <Layer {...backgroundLayerStyle} />
      <Layer {...iconLayerStyle} />
      <Layer {...labelLayerStyle} />
    </Source>
  );
});
